import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: no "@" alias on the Jest side (cf. admin/LanguageAwareField.jsx).
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { cn } from "../../lib/utils";

const COLOR_FIELDS = [
  ["primary_color", "brand.colorPrimary"],
  ["accent_color", "brand.colorAccent"],
  ["secondary_color", "brand.colorSecondary"],
];

/**
 * What a colour value is allowed to be, mirroring the write boundary
 * (`BrandingUpdate._check_hex_color`, backend/tenant_self_service.py): `#` plus 3, 4, 6
 * or 8 hex digits — the CSS forms, `#rgb` included, because practices seeded before the
 * native picker existed still hold three-digit values.
 */
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
export const isHexColor = (value) => HEX_COLOR.test((value || "").trim());

/**
 * `<input type="color">` accepts EXACTLY `#rrggbb` — it silently shows black for `#000`
 * or for an 8-digit value, so a stored short form has to be expanded before it is handed
 * over, or the swatch would misreport the colour the site is actually using.
 */
export const toPickerValue = (value) => {
  const v = (value || "").trim();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  if (/^#[0-9a-fA-F]{4}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return v.slice(0, 7);
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  return "#000000";
};

/**
 * ONE BRAND COLOUR (BUG-050).
 *
 * The row this replaces was a 48×40 swatch — which reads as a PREVIEW, not a control —
 * beside a free-text hex box that was the only thing on the row that looked interactive.
 * The box also wrote STRAIGHT into `branding`, so `"blue-ish"` was saved, the PUT
 * answered 200, and the public site silently kept its old colour: `useBrandingTheme`
 * hands the value to `CSSStyleDeclaration.setProperty`, which drops anything it cannot
 * parse. A save that reports success and changes nothing.
 *
 * So: the picker is the control (large, cursor-pointer, its own focus ring, and the whole
 * row opens it), the current value stays READABLE as text — a practitioner checking
 * against a brand guide needs to see `#1A2B3C`, not just the colour — and hex ENTRY moves
 * behind a disclosure, kept because a designer's exact code is a real requirement, but no
 * longer the default path.
 *
 * The typed value is a DRAFT: it reaches `branding` only once it parses as a colour, so a
 * half-typed `#1a2` cannot be saved, and the swatch keeps showing the last real colour
 * instead of flashing black on every keystroke.
 */
const ColorField = ({ colorKey, label, value, onChange, t }) => {
  const [draft, setDraft] = useState(value || "");

  // Re-seeded when the value changes from OUTSIDE this field — a preset click rewrites all
  // three colours at once, and a stale draft would then show a hex the site no longer uses.
  useEffect(() => { setDraft(value || ""); }, [value]);

  const invalid = draft.trim() !== "" && !isHexColor(draft);
  const commit = (next) => {
    setDraft(next);
    if (isHexColor(next)) onChange(next.trim());
  };

  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <label
        data-testid={`color-swatch-${colorKey}`}
        className="mt-1.5 flex items-center gap-3 rounded-xl border border-eh-border-soft p-2.5 cursor-pointer hover:bg-eh-cream transition focus-within:ring-2 focus-within:ring-eh-sage-deep"
      >
        <input
          type="color"
          value={toPickerValue(value)}
          onChange={(e) => { setDraft(e.target.value); onChange(e.target.value); }}
          data-testid={`color-${colorKey}`}
          aria-label={label}
          // `shrink-0`: the row is a flex, and without it the browser shrank the picker
          // to 44 px wide against its 56 px height — caught in the browser run, invisible
          // to jsdom, which reports no layout at all.
          className="w-14 h-14 shrink-0 rounded-lg border border-eh-border-soft cursor-pointer bg-transparent p-0"
        />
        <span className="min-w-0">
          <span className="block text-sm text-eh-ink">{t("brand.colorPickHint")}</span>
          <span className="block font-mono text-xs text-eh-muted" data-testid={`color-value-${colorKey}`}>
            {(value || "").toUpperCase()}
          </span>
        </span>
      </label>

      <details className="mt-2">
        <summary className="text-xs text-eh-muted cursor-pointer" data-testid={`hex-toggle-${colorKey}`}>
          {t("brand.hexToggle")}
        </summary>
        <div className="mt-2">
          <Label className="text-[11px] text-eh-muted">{t("brand.hexLabel")}</Label>
          <Input
            value={draft}
            onChange={(e) => commit(e.target.value)}
            placeholder="#1A2B3C"
            aria-invalid={invalid ? true : undefined}
            data-testid={`color-hex-${colorKey}`}
            className={cn("font-mono text-sm mt-1", invalid && invalidInputClass)}
          />
          <FieldError
            message={invalid ? t("brand.hexInvalid") : ""}
            testId={`color-hex-${colorKey}-error`}
          />
        </div>
      </details>
    </div>
  );
};

export default function PaletteCard({ branding, setBranding }) {
  const t = useT("admin");
  const PRESETS = [
    { name: t("brand.presetDefault"), primary: "#0B1020", accent: "#E6B85C", secondary: "#7FB8A4" },
    { name: "Sage forest", primary: "#1F2A24", accent: "#A8B589", secondary: "#D8DFCC" },
    { name: "Sand & ocean", primary: "#1A2B3C", accent: "#E8C39E", secondary: "#5B8CA8" },
    { name: "Plum dawn", primary: "#2A1F30", accent: "#E5A4B4", secondary: "#B49BC8" },
    { name: "Earth tones", primary: "#2D2418", accent: "#C9985B", secondary: "#9CA37C" },
  ];
  return (
    <div className="bg-white rounded-2xl border border-eh-border-soft p-6">
      <h2 className="font-display text-xl mb-4">{t("brand.palette")}</h2>
      <div className="flex gap-2 mb-6 flex-wrap">
        {PRESETS.map((p) => (
          <button
            key={p.name}
            data-testid={`preset-${p.name.toLowerCase().replace(/\s/g, '-')}`}
            onClick={() => setBranding({ ...branding, primary_color: p.primary, accent_color: p.accent, secondary_color: p.secondary })}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-eh-border-soft hover:bg-eh-cream text-xs"
          >
            <div className="flex gap-1">
              <div className="w-4 h-4 rounded-full" style={{ background: p.primary }} />
              <div className="w-4 h-4 rounded-full" style={{ background: p.accent }} />
              <div className="w-4 h-4 rounded-full" style={{ background: p.secondary }} />
            </div>
            {p.name}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {COLOR_FIELDS.map(([key, labelKey]) => (
          <ColorField
            key={key}
            colorKey={key}
            label={t(labelKey)}
            value={branding[key] || ""}
            onChange={(next) => setBranding({ ...branding, [key]: next })}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}
